// components/shared/reviews/ReviewForm.tsx
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Save, Star } from "lucide-react";
import { BaseReview, Review } from "../../../types/features/Review";
import { kafintech_products } from "../../../constantes/kafintech-product";
import { useProduct } from "../../../hooks/core/useProduct";

interface ReviewFormProps {
  initialData?: Review;
  isEdit?: boolean;
  onSubmit: (data: BaseReview) => Promise<void>;
  isLoading?: boolean;
  isSuccess: boolean;
}

export default function ReviewForm({
  initialData,
  isEdit = false,
  onSubmit,
  isLoading = false,
  isSuccess,
}: ReviewFormProps) {
  const navigate = useNavigate();
  const [hoverRating, setHoverRating] = useState(0);
  const {selectedProduct} = useProduct()
  const [formData, setFormData] = useState<BaseReview>({
    user_name: "",
    user_image: null,
    rating: 5,
    comment: "",
    comment_en: null,
    platform: "play_store",
    original_date: new Date().toISOString().slice(0, 16),
    is_visible: true,
    product_name: selectedProduct || "all",
    product_name_en: selectedProduct || "all",
  });

  useEffect(() => {
    if (initialData) {
      setFormData({
        user_name: initialData.user_name,
        user_image: initialData.user_image,
        rating: initialData.rating,
        comment: initialData.comment,
        comment_en: initialData.comment_en,
        platform: initialData.platform,
        original_date: initialData.original_date.slice(0, 16),
        is_visible: initialData.is_visible,
        product_name: initialData.product_name || "all",
        product_name_en: initialData.product_name_en || "all",
      });
    }
  }, [initialData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckbox = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    if (isSuccess) {
      setFormData({
        user_name: "",
        user_image: null,
        rating: 5,
        comment: "",
        comment_en: null,
        platform: "play_store",
        original_date: new Date().toISOString().slice(0, 16),
        is_visible: true,
        product_name: "all",
        product_name_en: "all",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        {/* 📦 Produit concerné */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Produit concerné <span className="text-red-500">*</span>
          </label>
          <select
            name="product_name"
            value={formData.product_name || "all"}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary"
          >
            <option value="all">Tous les produits</option>
            {kafintech_products.map((product) => (
              <option key={product.value} value={product.value}>
                {product.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Sélectionnez "Tous les produits" si cet avis concerne tous les produits
          </p>
        </div>

        {/* Infos utilisateur */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Informations utilisateur</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
              <input
                type="text"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Jean Dupont"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Photo (URL)</label>
              <input
                type="url"
                name="user_image"
                value={formData.user_image || ""}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="https://..."
              />
              {formData.user_image && (
                <div className="mt-2">
                  <img src={formData.user_image} alt="Aperçu" className="w-12 h-12 rounded-full object-cover" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Note */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Note *</label>
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, rating: star }))}
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                className="focus:outline-none"
              >
                <Star
                  size={32}
                  className={`transition-colors ${
                    star <= (hoverRating || formData.rating) ? "fill-yellow-400 text-yellow-400" : "text-gray-300"
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        {/* Plateforme */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Plateforme *</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="platform"
                value="play_store"
                checked={formData.platform === "play_store"}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span>Play Store</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="platform"
                value="app_store"
                checked={formData.platform === "app_store"}
                onChange={handleChange}
                className="w-4 h-4"
              />
              <span>App Store</span>
            </label>
          </div>
        </div>

        {/* Avis */}
        <div className="mb-6">
          <div className="border-b border-gray-200 mb-4 pb-2">
            <h2 className="text-lg font-semibold text-gray-900">Avis</h2>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire (FR) *</label>
              <textarea
                name="comment"
                value={formData.comment}
                onChange={handleChange}
                required
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Super application !"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Commentaire (EN)</label>
              <textarea
                name="comment_en"
                value={formData.comment_en || ""}
                onChange={handleChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
                placeholder="Great app !"
              />
            </div>
          </div>
        </div>

        {/* Date */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-1">Date de l'avis original *</label>
          <input
            type="date"
            name="original_date"
            value={formData.original_date}
            onChange={handleChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/50"
          />
        </div>

        {/* Visibilité */}
        <div className="mb-6">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="is_visible"
              checked={formData.is_visible}
              onChange={handleCheckbox}
              className="w-4 h-4 rounded"
            />
            <span className="text-sm font-medium text-gray-700">Afficher cet avis sur le site</span>
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {isLoading ? "Enregistrement..." : isEdit ? "Mettre à jour" : "Créer"}
        </button>
      </div>
    </form>
  );
}